import { ShieldBan } from 'lucide-react';
import type { OpsUsageRequestAdmission } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import { Chip } from '../ui/index.ts';

/**
 * The cc-lane admission interception, on the operator request surfaces
 * (router#1609, identity fields #1689, rendered #1615).
 *
 * Until #1615 the verdict was stored, projected, and read by nobody: the
 * operator who armed the switch could only answer "which rule refused this
 * request" by hand-querying `request_lifecycle_events`. These two components
 * are the answer on the surfaces they already use — a mark on the row so an
 * interception is findable while scanning, and the five fields on the detail
 * so it is explicable once found.
 *
 * `warn`, not `bad`: an interception is a configured switch doing its job, so
 * it must not read like a router fault. But it is also the reason a 4xx row
 * carries zero tokens and a `completed` lifecycle — the one pairing in the
 * product that otherwise reads as a contradiction — so it must not read like
 * an ordinary refusal either.
 */

export function AdmissionInterceptionBadge({
  admission,
  testId,
}: {
  readonly admission: OpsUsageRequestAdmission;
  readonly testId?: string;
}): JSX.Element {
  const t = useT();
  return (
    <Chip
      tone="warn"
      lamp={false}
      testId={testId}
      className="max-w-full gap-1"
      // The rule name alone is the scanning unit; the action and the identity
      // are one row-expand away. A title carries the pair for a hover, since
      // the cell is narrow enough that the rule name is what fits.
    >
      <ShieldBan aria-hidden="true" className="h-3 w-3 shrink-0" />
      <span className="min-w-0 truncate font-mono" title={interceptionTitle(admission, t)}>
        {admission.blacklistRule}
      </span>
    </Chip>
  );
}

function interceptionTitle(
  admission: OpsUsageRequestAdmission,
  t: ReturnType<typeof useT>,
): string {
  return `${t('requests.admission.rule')}: ${admission.blacklistRule} · ${t(
    'requests.admission.action',
  )}: ${admission.blacklistAction}`;
}

/**
 * All five fields as one titled block.
 *
 * It owns its own heading and `<dl>` rather than emitting loose `<dt>`/`<dd>`
 * pairs into the host's list, for two reasons. A heading cannot live inside a
 * `<dl>` — only `<dt>`, `<dd>` and grouping `<div>`s may — and dropping five
 * unannounced fields into a flat detail grid buries the one block on the panel
 * that explains why the request has no tokens. Each host supplies its own
 * chrome through `className`, since the two detail surfaces frame their
 * sections differently.
 *
 * The identity three render `—` when absent rather than being omitted, and
 * that distinction is load-bearing: absent means the row predates #1689, not
 * that the classifier had no opinion. A field that disappears would read as
 * the second.
 */
export function AdmissionInterceptionPanel({
  admission,
  idBase,
  className,
}: {
  readonly admission: OpsUsageRequestAdmission;
  readonly idBase: string;
  readonly className?: string;
}): JSX.Element {
  const t = useT();
  return (
    <section className={className} data-testid={idBase}>
      <h3 className="m-0 text-section text-paper">{t('requests.admission.title')}</h3>
      <dl className="mt-2 grid gap-3 text-dense md:grid-cols-2 xl:grid-cols-5">
        <AdmissionField
          label={t('requests.admission.rule')}
          value={admission.blacklistRule}
          testId={`${idBase}-rule`}
        />
        <AdmissionField
          label={t('requests.admission.action')}
          value={admission.blacklistAction}
          testId={`${idBase}-action`}
        />
        <AdmissionField
          label={t('requests.admission.client_kind')}
          value={admission.clientKind ?? '—'}
          testId={`${idBase}-client-kind`}
        />
        <AdmissionField
          label={t('requests.admission.client_entrypoint')}
          value={admission.clientEntrypoint ?? '—'}
          testId={`${idBase}-client-entrypoint`}
        />
        <AdmissionField
          label={t('requests.admission.lane')}
          value={admission.lane ?? '—'}
          testId={`${idBase}-lane`}
        />
      </dl>
    </section>
  );
}

function AdmissionField({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div>
      <dt className="text-label text-paper-low">{label}</dt>
      <dd className="mt-1 break-all font-mono text-dense text-paper" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}
